import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../api/staff'
import { TaxCertificatePreview } from './TaxCertificatePreview'
import {
  CorpAgreementProposalForm,
  DEFAULT_CORP,
  toCorpManualVariables,
  type CorpVars,
} from './CorpAgreementProposalForm'
import type { AuthSession } from '../../types/plans'

/**
 * 03.05.26: модуль «Документы» (рассрочка / налоговая справка) staff-LK.
 * 08.05.26: InstallmentProposalForm с таблицей платежей, TaxCertificateProposalForm
 *           с кодами 1/2, конвертация ₽→копейки перед отправкой в МИС.
 */

type DocumentClass = 'installment_agreement' | 'tax_certificate' | 'corp_agreement' | 'corp_agreement'
type StaffPermissionFlags = { canView: boolean; canCreate: boolean }

const DOCUMENT_CLASS_LABELS: Record<DocumentClass, string> = {
  installment_agreement: 'Договор рассрочки',
  tax_certificate: 'Налоговая справка',
  corp_agreement: 'Корп. договор',
}

const AI_HINTS: Record<DocumentClass, string> = {
  installment_agreement:
    'Опиши условия рассрочки своими словами: общая сумма, первый платёж, число месяцев, ' +
    'график (например, ежемесячно 25-го числа). AI заполнит структурированные поля.',
  corp_agreement: 'Корпоративный договор — ручное заполнение без AI.',
  tax_certificate:
    'Опиши период справки и ключевые суммы (код 1 и код 2). AI ' +
    'предложит черновик с готовой структурой и описанием услуг.',
}

interface GeneratedDocument {
  generated_document_id?: string
  id?: string
  document_id?: string
  document_class?: string
  template_label?: string
  status?: string
  created_at?: string
  patient_full_name?: string
  patient_pcode?: string
  human_status?: string
  patient_id?: string
  source_refs?: { patient_full_name?: string; patient_id?: string }
  metadata?: { template_name?: string; document_class_label?: string; lifecycle?: { delivery_status?: string } }
}

interface PatientSuggestion {
  misId: string
  fullName: string
}

interface InstallmentRow {
  month: number
  date: string
  amount_kopeks: number
}

interface InstallmentVars {
  total_rub: number
  first_payment_rub: number
  months: number
  schedule_day: number
  start_next_month: boolean
  agreement_number: string
  agreement_date: string
  agreement_basis: string
  guarantor_note: string
  rows: InstallmentRow[]
}

interface TaxVars {
  period_start: string
  period_end: string
  code1_rub: number
  code2_rub: number
  services_summary: string
  certificate_number: string
  certificate_date: string
  certificate_purpose: string
  issuer_note: string
  payer_full_name: string
  payer_inn: string
  payer_passport_series: string
  payer_passport_number: string
  payer_passport_issue_date: string
  payer_passport_issuer: string
  payer_passport_department_code: string
  services_patient_different: boolean
  services_patient_mis_id: string
  services_patient_full_name: string
  services_patient_inn: string
  services_patient_birth_date: string
  services_passport_series: string
  services_passport_number: string
  services_passport_issue_date: string
  services_passport_issuer: string
  finance_source_mis_id: string
  finance_source_label: string
}

interface Proposal {
  manual_variables?: Record<string, unknown>
  document_subject?: string
  source?: string
}

// ─── helpers ──────────────────────────────────────────────────────────────────────────────

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim())
}

/** Конвертирует DD.MM.YYYY → YYYY-MM-DD; возвращает строку ISO если уже ISO. */
function parseDateSafe(s: string): string {
  if (!s) return new Date().toISOString().slice(0, 10)
  const ddmmyyyy = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s)
  if (ddmmyyyy) return `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const d = new Date(s)
  return isNaN(d.getTime()) ? new Date().toISOString().slice(0, 10) : d.toISOString().slice(0, 10)
}

function extractPatientSuggestions(docs: GeneratedDocument[]): PatientSuggestion[] {
  const seen = new Set<string>()
  const result: PatientSuggestion[] = []
  for (const doc of docs) {
    const misId = doc.patient_id ?? doc.source_refs?.patient_id ?? ''
    const fullName = doc.patient_full_name ?? doc.source_refs?.patient_full_name ?? ''
    if (misId && fullName && !seen.has(misId)) {
      seen.add(misId)
      result.push({ misId, fullName })
    }
  }
  return result
}

function deriveFlags(permissions: string[] | undefined, roleKey?: string): StaffPermissionFlags {
  const granted = new Set(permissions ?? [])
  const isAdmin =
    roleKey === 'admin' ||
    roleKey === 'superadmin' ||
    granted.has('admin') ||
    granted.has('*') ||
    granted.has('all')
  return {
    canView: isAdmin || granted.has('documents') || granted.has('documents_create'),
    canCreate: isAdmin || granted.has('documents_create'),
  }
}

/** startNextMonth=true → первый платёж со следующего месяца (default); false → с текущего */
function buildRowDates(agreementDate: string, months: number, scheduleDay: number, startNextMonth = true): string[] {
  const dates: string[] = []
  const safeDate = parseDateSafe(agreementDate || new Date().toISOString().slice(0, 10))
  const base = new Date(safeDate)
  const offset = startNextMonth ? 1 : 0
  for (let i = 0; i < months; i++) {
    const d = new Date(base)
    d.setMonth(d.getMonth() + i + offset)
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
    d.setDate(Math.min(scheduleDay, lastDay))
    dates.push(d.toISOString().slice(0, 10))
  }
  return dates
}

function buildInstallmentRows(
  financedKopeks: number,
  months: number,
  agreementDate: string,
  scheduleDay: number,
  startNextMonth = true,
): InstallmentRow[] {
  const count = Math.max(1, Math.min(months, 120))
  const perRow = count > 0 ? Math.floor(financedKopeks / count) : 0
  const extra = financedKopeks - perRow * count
  const dates = buildRowDates(agreementDate, count, scheduleDay, startNextMonth)
  return Array.from({ length: count }, (_, i) => ({
    month: i + 1,
    date: dates[i] ?? '',
    amount_kopeks: i === count - 1 ? perRow + extra : perRow,
  }))
}

function redistributeAfter(
  rows: InstallmentRow[],
  changedIdx: number,
  newAmountKopeks: number,
  totalFinancedKopeks: number,
): InstallmentRow[] {
  const updated = rows.map((r, i) =>
    i === changedIdx ? { ...r, amount_kopeks: newAmountKopeks } : r,
  )
  const fixedSum = updated.slice(0, changedIdx + 1).reduce((s, r) => s + r.amount_kopeks, 0)
  const remaining = totalFinancedKopeks - fixedSum
  const countAfter = updated.length - changedIdx - 1
  if (countAfter <= 0) return updated
  const perRow = remaining >= 0 ? Math.floor(remaining / countAfter) : 0
  const lastExtra = remaining >= 0 ? remaining - perRow * countAfter : 0
  return updated.map((r, i) => {
    if (i <= changedIdx) return r
    const isLast = i === updated.length - 1
    return { ...r, amount_kopeks: Math.max(0, perRow + (isLast ? lastExtra : 0)) }
  })
}


function coerceAiMinorToKopeks(mv: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  const values = keys.map((k) => Number(mv[k] ?? 0)).filter((v) => v > 0)
  if (values.length === 0 || Math.max(...values) >= 1_000_000) return mv
  const out = { ...mv }
  for (const key of keys) {
    const v = Number(out[key] ?? 0)
    if (v > 0) out[key] = v * 100
  }
  return out
}

function initInstallmentVars(mv: Record<string, unknown>): InstallmentVars {
  mv = coerceAiMinorToKopeks(mv, [
    "installment_total_minor",
    "initial_payment_minor",
    "financed_amount_minor",
    "monthly_payment_minor",
  ])
  const totalKopeks = Number(mv.installment_total_minor ?? 0)
  const firstKopeks = Number(mv.initial_payment_minor ?? 0)
  const financedKopeks = Number(mv.financed_amount_minor ?? Math.max(0, totalKopeks - firstKopeks))
  const months = Math.max(1, Number(mv.installment_month_count ?? 12))
  const day = Math.max(1, Math.min(28, Number(mv.schedule_day ?? 25)))
  const agreementDate = parseDateSafe(String(mv.agreement_date ?? new Date().toISOString().slice(0, 10)))
  const startNextMonth = true
  return {
    total_rub: totalKopeks / 100,
    first_payment_rub: firstKopeks / 100,
    months,
    schedule_day: day,
    start_next_month: startNextMonth,
    agreement_number: String(mv.agreement_number ?? '[ЗАПОЛНИТЬ]'),
    agreement_date: agreementDate,
    agreement_basis: String(mv.agreement_basis ?? ''),
    guarantor_note: String(mv.guarantor_note ?? ''),
    rows: buildInstallmentRows(financedKopeks, months, agreementDate, day, startNextMonth),
  }
}

function initTaxVars(mv: Record<string, unknown>): TaxVars {
  mv = coerceAiMinorToKopeks(mv, [
    "deduction_code_1_minor",
    "deduction_code_2_minor",
    "deduction_total_minor",
    "payment_total_minor",
  ])
  const prevYear = new Date().getFullYear() - 1
  return {
    payer_full_name: String(mv.fns_payer_full_name ?? mv.payer_full_name ?? ''),
    payer_inn: String(mv.fns_payer_inn ?? mv.payer_inn ?? ''),
    payer_passport_series: String(mv.fns_payer_passport_series ?? ''),
    payer_passport_number: String(mv.fns_payer_passport_number ?? ''),
    payer_passport_issue_date: String(mv.fns_payer_passport_issue_date ?? ''),
    payer_passport_issuer: String(mv.fns_payer_passport_issuer ?? ''),
    payer_passport_department_code: String(mv.fns_payer_passport_department_code ?? ''),
    services_patient_different: Boolean(mv.services_patient_different ?? false),
    services_patient_mis_id: String(mv.certificate_patient_id ?? mv.services_patient_mis_id ?? ''),
    services_patient_full_name: String(mv.fns_services_patient_full_name ?? ''),
    services_patient_inn: String(mv.fns_services_inn ?? ''),
    services_patient_birth_date: String(mv.fns_services_birth_date ?? ''),
    services_passport_series: String(mv.fns_services_passport_series ?? ''),
    services_passport_number: String(mv.fns_services_passport_number ?? ''),
    services_passport_issue_date: String(mv.fns_services_passport_issue_date ?? ''),
    services_passport_issuer: String(mv.fns_services_passport_issuer ?? ''),
    finance_source_mis_id: String(mv.payment_source_patient_id ?? ''),
    finance_source_label: String(mv.finance_source_label ?? ''),
    period_start: String(mv.certificate_period_start ?? `${prevYear}-01-01`),
    period_end: String(mv.certificate_period_end ?? `${prevYear}-12-31`),
    code1_rub: Number(mv.deduction_code_1_minor ?? 0) / 100,
    code2_rub: Number(mv.deduction_code_2_minor ?? 0) / 100,
    services_summary: String(mv.services_summary ?? '[ЗАПОЛНИТЬ: перечень оказанных услуг]'),
    certificate_number: String(mv.certificate_number ?? '[ЗАПОЛНИТЬ]'),
    certificate_date: String(mv.certificate_date ?? new Date().toISOString().slice(0, 10)),
    certificate_purpose: String(mv.certificate_purpose ?? ''),
    issuer_note: String(mv.issuer_note ?? ''),
  }
}

function toInstallmentManualVariables(vars: InstallmentVars): Record<string, unknown> {
  const totalKopeks = Math.round(vars.total_rub * 100)
  const firstKopeks = Math.round(vars.first_payment_rub * 100)
  const financedKopeks = Math.max(0, totalKopeks - firstKopeks)
  const monthlyKopeks =
    vars.months > 0 ? Math.round(financedKopeks / vars.months) : 0
  const scheduleLines = vars.rows
    .map((r) => `${r.date}: ${(r.amount_kopeks / 100).toFixed(2)} ₽`)
    .join('; ')
  return {
    installment_total_minor: totalKopeks,
    initial_payment_minor: firstKopeks,
    financed_amount_minor: financedKopeks,
    installment_month_count: vars.months,
    monthly_payment_minor: monthlyKopeks,
    payment_schedule_summary: scheduleLines || `ежемесячно ${vars.schedule_day}-го числа`,
    agreement_number: vars.agreement_number,
    agreement_date: vars.agreement_date,
    agreement_basis: vars.agreement_basis || 'Оказание стоматологических услуг',
    guarantor_note: vars.guarantor_note,
  }
}

function toTaxManualVariables(vars: TaxVars): Record<string, unknown> {
  const code1 = Math.round(vars.code1_rub * 100)
  const code2 = Math.round(vars.code2_rub * 100)
  const total = code1 + code2
  return {
    deduction_code_1_minor: code1,
    deduction_code_2_minor: code2,
    deduction_total_minor: total,
    payment_total_minor: total,
    certificate_period_start: vars.period_start,
    certificate_period_end: vars.period_end,
    services_summary: vars.services_summary,
    certificate_number: vars.certificate_number,
    certificate_date: vars.certificate_date,
    certificate_purpose: vars.certificate_purpose,
    issuer_note: vars.issuer_note,
    payment_source_patient_id: vars.finance_source_mis_id || null,
    certificate_patient_id: vars.services_patient_different
      ? vars.services_patient_mis_id || null
      : null,
    certificate_payer_patient_id: null,
    fns_payer_full_name: vars.payer_full_name,
    fns_payer_inn: vars.payer_inn,
    fns_payer_passport_series: vars.payer_passport_series,
    fns_payer_passport_number: vars.payer_passport_number,
    fns_payer_passport_issue_date: vars.payer_passport_issue_date,
    fns_payer_passport_issuer: vars.payer_passport_issuer,
    fns_payer_passport_department_code: vars.payer_passport_department_code,
    fns_services_patient_full_name: vars.services_patient_different
      ? vars.services_patient_full_name
      : '',
    fns_services_inn: vars.services_patient_different ? vars.services_patient_inn : '',
    fns_services_birth_date: vars.services_patient_different
      ? vars.services_patient_birth_date
      : '',
    fns_services_passport_series: vars.services_patient_different
      ? vars.services_passport_series
      : '',
    fns_services_passport_number: vars.services_patient_different
      ? vars.services_passport_number
      : '',
    fns_services_passport_issue_date: vars.services_patient_different
      ? vars.services_passport_issue_date
      : '',
    fns_services_passport_issuer: vars.services_patient_different
      ? vars.services_passport_issuer
      : '',
    services_patient_different: vars.services_patient_different,
    finance_source_label: vars.finance_source_label,
  }
}

// ─── sub-components ─────────────────────────────────────────────────────────────────────────────

function InstallmentProposalForm({
  vars,
  onChange,
}: {
  vars: InstallmentVars
  onChange: (v: InstallmentVars) => void
}) {
  const financedRub = Math.max(0, vars.total_rub - vars.first_payment_rub)
  const monthlyRub = vars.months > 0 ? financedRub / vars.months : 0
  const totalFinancedKopeks = Math.round(financedRub * 100)

  const rebuild = (
    total: number,
    first: number,
    months: number,
    day: number,
    date: string,
    startNextMonth: boolean,
  ): InstallmentRow[] => {
    const financed = Math.max(0, Math.round((total - first) * 100))
    return buildInstallmentRows(financed, Math.max(1, months), date, day, startNextMonth)
  }

  return (
    <div style={proposalFormStyle}>
      <p style={proposalFormTitleStyle}>Условия рассрочки</p>
      <div style={formGridStyle}>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>Сумма по рассрочке (₽)</span>
          <input
            type="number"
            min={0}
            step={100}
            value={vars.total_rub || ''}
            onChange={(e) => {
              const v = parseFloat(e.target.value) || 0
              const rows = rebuild(v, vars.first_payment_rub, vars.months, vars.schedule_day, vars.agreement_date, vars.start_next_month)
              onChange({ ...vars, total_rub: v, rows })
            }}
            style={formInputStyle}
          />
        </label>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>Первый платёж (₽)</span>
          <input
            type="number"
            min={0}
            step={100}
            value={vars.first_payment_rub || ''}
            onChange={(e) => {
              const v = parseFloat(e.target.value) || 0
              const rows = rebuild(vars.total_rub, v, vars.months, vars.schedule_day, vars.agreement_date, vars.start_next_month)
              onChange({ ...vars, first_payment_rub: v, rows })
            }}
            style={formInputStyle}
          />
        </label>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>Месяцев</span>
          <input
            type="number"
            min={1}
            max={120}
            value={vars.months || ''}
            onChange={(e) => {
              const v = parseInt(e.target.value) || 1
              const rows = rebuild(vars.total_rub, vars.first_payment_rub, v, vars.schedule_day, vars.agreement_date, vars.start_next_month)
              onChange({ ...vars, months: v, rows })
            }}
            style={formInputStyle}
          />
        </label>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>День платежа (1–28)</span>
          <input
            type="number"
            min={1}
            max={28}
            value={vars.schedule_day || ''}
            onChange={(e) => {
              const v = Math.max(1, Math.min(28, parseInt(e.target.value) || 25))
              const rows = rebuild(vars.total_rub, vars.first_payment_rub, vars.months, v, vars.agreement_date, vars.start_next_month)
              onChange({ ...vars, schedule_day: v, rows })
            }}
            style={formInputStyle}
          />
        </label>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>Дата договора</span>
          <input
            type="date"
            value={vars.agreement_date || ''}
            onChange={(e) => {
              const rows = rebuild(vars.total_rub, vars.first_payment_rub, vars.months, vars.schedule_day, e.target.value, vars.start_next_month)
              onChange({ ...vars, agreement_date: e.target.value, rows })
            }}
            style={formInputStyle}
          />
        </label>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>Номер договора</span>
          <input
            value={vars.agreement_number || ''}
            onChange={(e) => onChange({ ...vars, agreement_number: e.target.value })}
            style={formInputStyle}
          />
        </label>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>Основание договора</span>
          <input
            value={vars.agreement_basis || ''}
            onChange={(e) => onChange({ ...vars, agreement_basis: e.target.value })}
            placeholder="Оказание стоматологических услуг"
            style={formInputStyle}
          />
        </label>
      </div>
      {/* Start month toggle */}
      <div style={calcRowStyle}>
        <span style={{ fontSize: '0.8rem', color: '#4b5563' }}>Первый платёж:</span>
        <button
          type="button"
          onClick={() => {
            const snm = !vars.start_next_month
            const rows = rebuild(vars.total_rub, vars.first_payment_rub, vars.months, vars.schedule_day, vars.agreement_date, snm)
            onChange({ ...vars, start_next_month: snm, rows })
          }}
          style={vars.start_next_month ? toggleActiveStyle : toggleStyle}
        >
          {vars.start_next_month ? 'Со следующего месяца' : 'С этого месяца'}
        </button>
      </div>
      <div style={calcRowStyle}>
        <span>
          Финансируется:{' '}
          <strong>{financedRub.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽</strong>
        </span>
        <span>
          Ежемесячно ~
          <strong>{monthlyRub.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽</strong>
        </span>
      </div>
      {vars.rows.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <p style={scheduleHintStyle}>
            График платежей — редактируйте суммы, остальные пересчитаются автоматически:
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={scheduleTableStyle}>
              <thead>
                <tr>
                  <th style={scheduleThStyle}>№</th>
                  <th style={scheduleThStyle}>Дата</th>
                  <th style={scheduleThStyle}>Сумма ₽</th>
                </tr>
              </thead>
              <tbody>
                {vars.rows.map((row, idx) => (
                  <tr key={idx}>
                    <td style={scheduleTdStyle}>{row.month}</td>
                    <td style={scheduleTdStyle}>{row.date}</td>
                    <td style={scheduleTdStyle}>
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={(row.amount_kopeks / 100).toFixed(2)}
                        onChange={(e) => {
                          const kopeks = Math.max(0, Math.round(parseFloat(e.target.value || '0') * 100))
                          onChange({ ...vars, rows: redistributeAfter(vars.rows, idx, kopeks, totalFinancedKopeks) })
                        }}
                        style={scheduleInputStyle}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function TaxCertificateProposalForm({
  vars,
  onChange,
  onLoadFinancials,
  isLoadingFinancials,
  payerLabel,
}: {
  vars: TaxVars
  onChange: (v: TaxVars) => void
  onLoadFinancials?: () => Promise<void>
  isLoadingFinancials?: boolean
  payerLabel?: string
}) {
  const totalRub = vars.code1_rub + vars.code2_rub

  return (
    <div style={proposalFormStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
        <p style={proposalFormTitleStyle}>Данные налоговой справки (КНД 1151156)</p>
        {onLoadFinancials && (
          <button
            type="button"
            onClick={() => void onLoadFinancials()}
            disabled={isLoadingFinancials}
            style={isLoadingFinancials ? disabledButtonStyle : ghostButtonStyle}
          >
            {isLoadingFinancials ? 'Загружаем…' : 'Загрузить суммы из финансов'}
          </button>
        )}
      </div>

      <p style={{ ...mutedStyle, margin: '0 0 0.75rem', fontSize: '0.82rem' }}>
        Налогоплательщик (стр. 1): {payerLabel || vars.payer_full_name || 'выберите пациента'}
      </p>

      <details open style={{ marginBottom: '0.75rem' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: '0.5rem' }}>
          Источник финансов и пациент на стр. 2
        </summary>
        <div style={formGridStyle}>
          <label style={{ ...formFieldLabelStyle, gridColumn: '1 / -1' }}>
            <span style={formFieldNameStyle}>Карточка платежей (MIS UUID, если не плательщик)</span>
            <input
              value={vars.finance_source_mis_id || ''}
              placeholder="UUID пациента, с чьей карточки брать оплаты"
              onChange={(e) =>
                onChange({
                  ...vars,
                  finance_source_mis_id: e.target.value.trim(),
                  finance_source_label: e.target.value.trim() ? vars.finance_source_label : '',
                })
              }
              style={formInputStyle}
            />
          </label>
          <label style={{ ...formFieldLabelStyle, gridColumn: '1 / -1' }}>
            <span style={formFieldNameStyle}>Подпись источника финансов (для предпросмотра)</span>
            <input
              value={vars.finance_source_label || ''}
              placeholder="Например: дочь — Иванова Анна"
              onChange={(e) => onChange({ ...vars, finance_source_label: e.target.value })}
              style={formInputStyle}
            />
          </label>
          <label style={{ ...formFieldLabelStyle, gridColumn: '1 / -1', flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={vars.services_patient_different}
              onChange={(e) =>
                onChange({
                  ...vars,
                  services_patient_different: e.target.checked,
                  services_patient_mis_id: e.target.checked ? vars.services_patient_mis_id : '',
                  services_patient_full_name: e.target.checked ? vars.services_patient_full_name : '',
                })
              }
            />
            <span style={formFieldNameStyle}>Пациент ≠ налогоплательщик (заполнить стр. 2)</span>
          </label>
          {vars.services_patient_different && (
            <>
              <label style={{ ...formFieldLabelStyle, gridColumn: '1 / -1' }}>
                <span style={formFieldNameStyle}>Пациент (MIS UUID)</span>
                <input
                  value={vars.services_patient_mis_id || ''}
                  onChange={(e) => onChange({ ...vars, services_patient_mis_id: e.target.value.trim() })}
                  style={formInputStyle}
                />
              </label>
              <label style={formFieldLabelStyle}>
                <span style={formFieldNameStyle}>ФИО пациента (стр. 2)</span>
                <input
                  value={vars.services_patient_full_name || ''}
                  onChange={(e) => onChange({ ...vars, services_patient_full_name: e.target.value })}
                  style={formInputStyle}
                />
              </label>
              <label style={formFieldLabelStyle}>
                <span style={formFieldNameStyle}>ИНН пациента</span>
                <input
                  value={vars.services_patient_inn || ''}
                  onChange={(e) => onChange({ ...vars, services_patient_inn: e.target.value })}
                  style={formInputStyle}
                />
              </label>
              <label style={formFieldLabelStyle}>
                <span style={formFieldNameStyle}>Дата рождения</span>
                <input
                  type="date"
                  value={vars.services_patient_birth_date || ''}
                  onChange={(e) => onChange({ ...vars, services_patient_birth_date: e.target.value })}
                  style={formInputStyle}
                />
              </label>
              <label style={formFieldLabelStyle}>
                <span style={formFieldNameStyle}>Паспорт серия</span>
                <input
                  value={vars.services_passport_series || ''}
                  onChange={(e) => onChange({ ...vars, services_passport_series: e.target.value })}
                  style={formInputStyle}
                />
              </label>
              <label style={formFieldLabelStyle}>
                <span style={formFieldNameStyle}>Паспорт номер</span>
                <input
                  value={vars.services_passport_number || ''}
                  onChange={(e) => onChange({ ...vars, services_passport_number: e.target.value })}
                  style={formInputStyle}
                />
              </label>
              <label style={formFieldLabelStyle}>
                <span style={formFieldNameStyle}>Паспорт дата выдачи</span>
                <input
                  type="date"
                  value={vars.services_passport_issue_date || ''}
                  onChange={(e) => onChange({ ...vars, services_passport_issue_date: e.target.value })}
                  style={formInputStyle}
                />
              </label>
              <label style={{ ...formFieldLabelStyle, gridColumn: '1 / -1' }}>
                <span style={formFieldNameStyle}>Кем выдан</span>
                <input
                  value={vars.services_passport_issuer || ''}
                  onChange={(e) => onChange({ ...vars, services_passport_issuer: e.target.value })}
                  style={formInputStyle}
                />
              </label>
            </>
          )}
        </div>
      </details>

      <details open style={{ marginBottom: '0.75rem' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: '0.5rem' }}>
          Паспорт и ИНН налогоплательщика
        </summary>
        <div style={formGridStyle}>
          <label style={formFieldLabelStyle}>
            <span style={formFieldNameStyle}>ФИО (если нужно переопределить)</span>
            <input
              value={vars.payer_full_name || ''}
              onChange={(e) => onChange({ ...vars, payer_full_name: e.target.value })}
              style={formInputStyle}
            />
          </label>
          <label style={formFieldLabelStyle}>
            <span style={formFieldNameStyle}>ИНН</span>
            <input
              value={vars.payer_inn || ''}
              onChange={(e) => onChange({ ...vars, payer_inn: e.target.value })}
              style={formInputStyle}
            />
          </label>
          <label style={formFieldLabelStyle}>
            <span style={formFieldNameStyle}>Паспорт серия</span>
            <input
              value={vars.payer_passport_series || ''}
              onChange={(e) => onChange({ ...vars, payer_passport_series: e.target.value })}
              style={formInputStyle}
            />
          </label>
          <label style={formFieldLabelStyle}>
            <span style={formFieldNameStyle}>Паспорт номер</span>
            <input
              value={vars.payer_passport_number || ''}
              onChange={(e) => onChange({ ...vars, payer_passport_number: e.target.value })}
              style={formInputStyle}
            />
          </label>
          <label style={formFieldLabelStyle}>
            <span style={formFieldNameStyle}>Дата выдачи</span>
            <input
              type="date"
              value={vars.payer_passport_issue_date || ''}
              onChange={(e) => onChange({ ...vars, payer_passport_issue_date: e.target.value })}
              style={formInputStyle}
            />
          </label>
          <label style={formFieldLabelStyle}>
            <span style={formFieldNameStyle}>Код подразделения</span>
            <input
              value={vars.payer_passport_department_code || ''}
              onChange={(e) => onChange({ ...vars, payer_passport_department_code: e.target.value })}
              style={formInputStyle}
            />
          </label>
          <label style={{ ...formFieldLabelStyle, gridColumn: '1 / -1' }}>
            <span style={formFieldNameStyle}>Кем выдан</span>
            <input
              value={vars.payer_passport_issuer || ''}
              onChange={(e) => onChange({ ...vars, payer_passport_issuer: e.target.value })}
              style={formInputStyle}
            />
          </label>
        </div>
      </details>

      <div style={formGridStyle}>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>Период с</span>
          <input
            type="date"
            value={vars.period_start || ''}
            onChange={(e) => onChange({ ...vars, period_start: e.target.value })}
            style={formInputStyle}
          />
        </label>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>Период по</span>
          <input
            type="date"
            value={vars.period_end || ''}
            onChange={(e) => onChange({ ...vars, period_end: e.target.value })}
            style={formInputStyle}
          />
        </label>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>Код 1 — обычное лечение (₽)</span>
          <input
            type="number"
            min={0}
            step={0.01}
            value={vars.code1_rub || ''}
            onChange={(e) => onChange({ ...vars, code1_rub: parseFloat(e.target.value) || 0 })}
            style={formInputStyle}
          />
        </label>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>Код 2 — дорогостоящее лечение (₽)</span>
          <input
            type="number"
            min={0}
            step={0.01}
            value={vars.code2_rub || ''}
            onChange={(e) => onChange({ ...vars, code2_rub: parseFloat(e.target.value) || 0 })}
            style={formInputStyle}
          />
        </label>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>Дата справки</span>
          <input
            type="date"
            value={vars.certificate_date || ''}
            onChange={(e) => onChange({ ...vars, certificate_date: e.target.value })}
            style={formInputStyle}
          />
        </label>
        <label style={formFieldLabelStyle}>
          <span style={formFieldNameStyle}>Номер справки</span>
          <input
            value={vars.certificate_number || ''}
            onChange={(e) => onChange({ ...vars, certificate_number: e.target.value })}
            style={formInputStyle}
          />
        </label>
      </div>
      <div style={calcRowStyle}>
        <span>
          Итого к вычету:{' '}
          <strong>{totalRub.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽</strong>
        </span>
      </div>
      <label style={textareaFieldLabelStyle}>
        <span style={formFieldNameStyle}>Перечень оказанных услуг</span>
        <textarea
          value={vars.services_summary || ''}
          onChange={(e) => onChange({ ...vars, services_summary: e.target.value })}
          rows={3}
          style={textareaInlineStyle}
        />
      </label>

      <TaxCertificatePreview
        vars={vars}
        clinicName="ООО «ДИ ЭЙЧ ЦЕНТР»"
        clinicInn="7715438229"
      />
    </div>
  )
}


// ─── main component ──────────────────────────────────────────────────────────────────────────────────

export interface StaffDocumentsWorkspaceProps {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  permissions?: string[]
  roleKey?: string
}

const DEFAULT_INSTALLMENT: InstallmentVars = {
  total_rub: 0,
  first_payment_rub: 0,
  months: 12,
  schedule_day: 25,
  start_next_month: true,
  agreement_number: '[ЗАПОЛНИТЬ]',
  agreement_date: new Date().toISOString().slice(0, 10),
  agreement_basis: '',
  guarantor_note: '',
  rows: [],
}

const DEFAULT_TAX: TaxVars = {
  period_start: `${new Date().getFullYear() - 1}-01-01`,
  period_end: `${new Date().getFullYear() - 1}-12-31`,
  code1_rub: 0,
  code2_rub: 0,
  services_summary: '[ЗАПОЛНИТЬ: перечень оказанных услуг]',
  certificate_number: '[ЗАПОЛНИТЬ]',
  certificate_date: new Date().toISOString().slice(0, 10),
  certificate_purpose: '',
  issuer_note: '',
  payer_full_name: '',
  payer_inn: '',
  payer_passport_series: '',
  payer_passport_number: '',
  payer_passport_issue_date: '',
  payer_passport_issuer: '',
  payer_passport_department_code: '',
  services_patient_different: false,
  services_patient_mis_id: '',
  services_patient_full_name: '',
  services_patient_inn: '',
  services_patient_birth_date: '',
  services_passport_series: '',
  services_passport_number: '',
  services_passport_issue_date: '',
  services_passport_issuer: '',
  finance_source_mis_id: '',
  finance_source_label: '',
}

export function StaffDocumentsWorkspace({
  bootstrap,
  session,
  permissions,
  roleKey,
}: StaffDocumentsWorkspaceProps) {
  const flags = useMemo(() => deriveFlags(permissions, roleKey), [permissions, roleKey])
  const [documentClass, setDocumentClass] = useState<DocumentClass>('installment_agreement')

  // Patient search / selection
  const [patientQuery, setPatientQuery] = useState('')
  const [selectedPatientMisId, setSelectedPatientMisId] = useState('')
  const [selectedPatientName, setSelectedPatientName] = useState('')
  const [suggestions, setSuggestions] = useState<PatientSuggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // AI proposal inputs
  const [voiceText, setVoiceText] = useState('')
  const [aiMode, setAiMode] = useState<'fast' | 'deep'>('fast')

  // Smart form state
  const [installmentVars, setInstallmentVars] = useState<InstallmentVars>(DEFAULT_INSTALLMENT)
  const [taxVars, setTaxVars] = useState<TaxVars>(DEFAULT_TAX)
  const [corpVars, setCorpVars] = useState<CorpVars>(DEFAULT_CORP)
  const [proposal, setProposal] = useState<Proposal | null>(null)

  // Async state
  const [documents, setDocuments] = useState<GeneratedDocument[]>([])
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [listError, setListError] = useState<string | null>(null)
  const [isProposing, setIsProposing] = useState(false)
  const [proposeError, setProposeError] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)
  const [generatedNotice, setGeneratedNotice] = useState<string | null>(null)
  const [isLoadingFinancials, setIsLoadingFinancials] = useState(false)

  const effectivePatientMisId =
    selectedPatientMisId || (isUuidLike(patientQuery) ? patientQuery.trim() : '')

  useEffect(() => {
    if (documentClass !== 'tax_certificate') return
    const label = selectedPatientName ?? ''
    if (!effectivePatientMisId || !label) return
    setTaxVars((prev) => ({
      ...prev,
      payer_full_name: prev.payer_full_name || label,
    }))
  }, [documentClass, effectivePatientMisId, selectedPatientName])

  // Reset all form state when document class changes (bug: voiceText was leaking between tabs)
  useEffect(() => {
    setProposal(null)
    setInstallmentVars(DEFAULT_INSTALLMENT)
    setTaxVars(DEFAULT_TAX)
    setCorpVars(DEFAULT_CORP)
    setVoiceText('')
    setProposeError(null)
    setGenerateError(null)
    setGeneratedNotice(null)
  }, [documentClass])

  const loadDocuments = useCallback(async () => {
    if (!flags.canView) return
    setIsLoadingList(true)
    setListError(null)
    try {
      const query = patientQuery.trim()
      const listOptions: Parameters<typeof staffApi.listDocuments>[2] = {
        documentClass,
        limit: 50,
      }
      if (query) {
        if (isUuidLike(query)) {
          listOptions.patientId = query
        } else {
          listOptions.search = query
        }
      }
      const data = (await staffApi.listDocuments(
        bootstrap,
        session,
        listOptions,
      )) as Record<string, unknown> | null
      const items = Array.isArray(data?.items)
        ? (data?.items as GeneratedDocument[])
        : Array.isArray(data?.documents)
          ? (data?.documents as GeneratedDocument[])
          : Array.isArray(data)
            ? (data as GeneratedDocument[])
            : []
      setDocuments(items)
      if (query && !isUuidLike(query)) {
        const newSuggestions = extractPatientSuggestions(items)
        setSuggestions(newSuggestions)
        if (newSuggestions.length > 0) setShowSuggestions(true)
      }
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Не удалось загрузить документы')
      setDocuments([])
    } finally {
      setIsLoadingList(false)
    }
  }, [bootstrap, session, documentClass, patientQuery, flags.canView])

  useEffect(() => {
    void loadDocuments()
  }, [loadDocuments])

  const handlePropose = useCallback(async () => {
    if (!flags.canCreate) return
    setIsProposing(true)
    setProposeError(null)
    setProposal(null)
    try {
      const data = (await staffApi.aiProposeDocument(bootstrap, session, {
        document_class: documentClass,
        patient_id: effectivePatientMisId || undefined,
        voice_text: voiceText,
        ai_mode: aiMode,
      })) as Record<string, unknown> | null

      const rawData = (data as Record<string, unknown>) ?? {}
      // Support both new format (manual_variables) and old placeholder format (fields)
      const manualVars =
        (rawData.manual_variables as Record<string, unknown> | undefined) ??
        (rawData.fields as Record<string, unknown> | undefined) ??
        {}
      const source = String(rawData.source ?? 'mis_ai')
      const documentSubject = String(rawData.document_subject ?? '')

      setProposal({ manual_variables: manualVars, document_subject: documentSubject, source })

      if (documentClass === 'installment_agreement') {
        setInstallmentVars(initInstallmentVars(manualVars))
      } else {
        setTaxVars(initTaxVars(manualVars))
      }
    } catch (e) {
      setProposeError(e instanceof Error ? e.message : 'Не удалось получить proposal от AI')
    } finally {
      setIsProposing(false)
    }
  }, [bootstrap, session, documentClass, effectivePatientMisId, aiMode, voiceText, flags.canCreate])

  const handleGenerate = useCallback(async () => {
    if (!flags.canCreate) return
    if (documentClass !== 'corp_agreement') {
      if (!effectivePatientMisId) {
        setGenerateError('Выберите пациента из списка или введите его UUID из МИС.')
        return
      }
      if (!proposal) {
        setGenerateError('Сначала нажмите «Собрать proposal» — нужно получить поля документа перед генерацией.')
        return
      }
    }
    setIsGenerating(true)
    setGenerateError(null)
    setGeneratedNotice(null)
    const manualVariables =
      documentClass === 'installment_agreement'
        ? toInstallmentManualVariables(installmentVars)
        : documentClass === 'corp_agreement'
          ? toCorpManualVariables(corpVars)
          : toTaxManualVariables(taxVars)
    try {
      const generateBody: Record<string, unknown> = {
        document_class: documentClass,
        manual_variables: manualVariables,
        document_subject:
          documentClass === 'corp_agreement'
            ? corpVars.agreement_number
            : (proposal?.document_subject ?? ''),
      }
      if (documentClass !== 'corp_agreement') {
        generateBody.patient_id = effectivePatientMisId
      }
      if (documentClass === 'tax_certificate') {
        generateBody.certificate_payer_patient_id = effectivePatientMisId
        if (taxVars.services_patient_different && taxVars.services_patient_mis_id) {
          generateBody.certificate_patient_id = taxVars.services_patient_mis_id
        }
        if (taxVars.finance_source_mis_id) {
          generateBody.payment_source_patient_id = taxVars.finance_source_mis_id
        }
      }
      const data = (await staffApi.generateDocument(bootstrap, session, generateBody)) as Record<string, unknown> | null
      const documentId =
        (data?.generated_document_id as string | undefined) ??
        (data?.document_id as string | undefined) ??
        (data?.id as string | undefined)
      setGeneratedNotice(
        documentId
          ? `Документ создан в МИС, id=${documentId}. Он появится в списке ниже после обновления.`
          : 'Документ отправлен на генерацию в МИС, проверьте список ниже.',
      )
      void loadDocuments()
    } catch (e) {
      setGenerateError(e instanceof Error ? e.message : 'Не удалось сгенерировать документ')
    } finally {
      setIsGenerating(false)
    }
  }, [
    bootstrap,
    session,
    documentClass,
    effectivePatientMisId,
    proposal,
    installmentVars,
    taxVars,
    corpVars,
    flags.canCreate,
    loadDocuments,
  ])

  const handleDownload = useCallback(
    async (id: string) => {
      try {
        const { blob, contentDisposition } = await staffApi.downloadDocument(bootstrap, session, id)
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const filenameMatch = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(contentDisposition)
        a.download = filenameMatch
          ? decodeURIComponent(filenameMatch[1].replace(/"$/, ''))
          : `document-${id}.pdf`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      } catch (e) {
        alert(e instanceof Error ? e.message : 'Не удалось скачать документ')
      }
    },
    [bootstrap, session],
  )

  const handleLoadFinancials = useCallback(async () => {
    if (!effectivePatientMisId) {
      setGenerateError('Выберите пациента перед загрузкой финансовых данных.')
      return
    }
    setIsLoadingFinancials(true)
    setGenerateError(null)
    try {
      const data = (await staffApi.getPatientFinancialSummary(bootstrap, session, {
        patient_id: taxVars.finance_source_mis_id || effectivePatientMisId,
        period_start: taxVars.period_start,
        period_end: taxVars.period_end,
      })) as { code1_minor?: number; code2_minor?: number; services_list?: string[] } | null
      if (data) {
        setTaxVars((prev) => ({
          ...prev,
          code1_rub: (data.code1_minor ?? 0) / 100,
          code2_rub: (data.code2_minor ?? 0) / 100,
          services_summary: data.services_list?.length
            ? data.services_list.join(';\n')
            : prev.services_summary,
        }))
      }
    } catch (e) {
      setGenerateError(e instanceof Error ? e.message : 'Не удалось загрузить финансовые данные')
    } finally {
      setIsLoadingFinancials(false)
    }
  }, [bootstrap, session, effectivePatientMisId, taxVars.period_start, taxVars.period_end, taxVars.finance_source_mis_id])

  if (!flags.canView) {
    return (
      <section style={panelStyle}>
        <h2 style={titleStyle}>Документы</h2>
        <p style={mutedStyle}>
          У вас нет доступа к модулю «Документы». Попросите администратора выдать разрешение
          <code style={codeStyle}>documents</code> (просмотр) или
          <code style={codeStyle}>documents_create</code> (подготовка).
        </p>
      </section>
    )
  }

  const canGenerate =
    !isGenerating &&
    (documentClass === 'corp_agreement'
      ? Boolean(corpVars.agreement_number && corpVars.customer_legal_name && corpVars.customer_director_name)
      : Boolean(proposal && effectivePatientMisId))

  return (
    <section style={containerStyle}>
      <header style={headerStyle}>
        <div>
          <h2 style={titleStyle}>Документы</h2>
          <p style={mutedStyle}>
            Договоры и справки готовятся в МИС (LK — тонкий клиент). Документы хранятся в PostgreSQL тенанта.
          </p>
        </div>
        <div style={tabRowStyle}>
          {(Object.keys(DOCUMENT_CLASS_LABELS) as DocumentClass[]).map((cls) => (
            <button
              key={cls}
              type="button"
              onClick={() => setDocumentClass(cls)}
              style={cls === documentClass ? activeTabStyle : tabStyle}
            >
              {DOCUMENT_CLASS_LABELS[cls]}
            </button>
          ))}
        </div>
      </header>

      <div style={gridStyle}>
        {documentClass !== 'corp_agreement' && (
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>Контекст пациента</h3>
          <label style={labelStyle}>
            Пациент
            <div style={{ position: 'relative' }}>
              <input
                value={patientQuery}
                onChange={(e) => {
                  const val = e.target.value
                  setPatientQuery(val)
                  setSelectedPatientMisId('')
                  setSelectedPatientName('')
                  setSuggestions([])
                  setShowSuggestions(false)
                  if (debounceRef.current) clearTimeout(debounceRef.current)
                  debounceRef.current = setTimeout(() => { /* loadDocuments via useEffect */ }, 400)
                }}
                onKeyDown={(e) => { if (e.key === 'Escape') setShowSuggestions(false) }}
                placeholder="ФИО пациента или UUID из МИС"
                style={inputStyle}
                spellCheck={false}
                autoComplete="off"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div style={suggestionsStyle}>
                  {suggestions.map((s) => (
                    <button
                      key={s.misId}
                      type="button"
                      style={suggestionItemStyle}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        setPatientQuery(s.fullName)
                        setSelectedPatientMisId(s.misId)
                        setSelectedPatientName(s.fullName)
                        setShowSuggestions(false)
                      }}
                    >
                      <span>{s.fullName}</span>
                      <span style={suggestionIdStyle}>{s.misId.slice(0, 8)}…</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </label>
          {selectedPatientMisId ? (
            <p style={patientSelectedStyle}>
              ✓ {selectedPatientName}{' '}
              <span style={mutedSmallStyle}>({selectedPatientMisId.slice(0, 8)}…)</span>
            </p>
          ) : patientQuery && !isUuidLike(patientQuery) ? (
            <p style={mutedStyle}>
              Поиск по ФИО — документы фильтруются. Выберите пациента из списка ниже для создания
              документа.
            </p>
          ) : (
            <p style={mutedStyle}>
              Введите ФИО или UUID пациента из МИС. Документы сохранятся в карточке пациента.
            </p>
          )}
        </div>
        )}

        {/* AI prefill card */}
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>{documentClass === 'corp_agreement' ? 'Корпоративный договор' : 'AI-предзаполнение'}</h3>
          <p style={mutedStyle}>{AI_HINTS[documentClass]}</p>
          {documentClass !== 'corp_agreement' && (
          <label style={labelStyle}>
            Данные для документа
            <textarea
              value={voiceText}
              onChange={(e) => setVoiceText(e.target.value)}
              placeholder="Опишите условия рассрочки или содержание справки своими словами"
              style={textareaStyle}
              rows={4}
            />
          </label>
          )}
          <div style={inlineRowStyle}>
            {documentClass !== 'corp_agreement' && (
            <label style={inlineLabelStyle}>
              Режим AI
              <select
                value={aiMode}
                onChange={(e) => setAiMode(e.target.value as 'fast' | 'deep')}
                style={selectStyle}
              >
                <option value="fast">Быстро (DeepSeek)</option>
                <option value="deep">Глубоко (GPT-5.2)</option>
              </select>
            </label>
            )}
            {documentClass !== 'corp_agreement' && (
            <button
              type="button"
              onClick={() => void handlePropose()}
              disabled={!flags.canCreate || isProposing}
              style={primaryButtonStyle}
            >
              {isProposing ? 'AI собирает proposal…' : 'Собрать proposal'}
            </button>
            )}
            <button
              type="button"
              onClick={() => void handleGenerate()}
              disabled={!canGenerate}
              style={canGenerate ? successButtonStyle : disabledButtonStyle}
              title={
                documentClass === 'corp_agreement'
                  ? !corpVars.agreement_number
                    ? 'Укажите номер договора'
                    : undefined
                  : !effectivePatientMisId
                    ? 'Выберите пациента'
                    : !proposal
                      ? 'Сначала соберите proposal'
                      : undefined
              }
            >
              {isGenerating ? 'Генерация…' : 'Сгенерировать документ'}
            </button>
          </div>
          {!flags.canCreate && (
            <p style={warningStyle}>
              У вас есть только просмотр (documents). Создание требует разрешения documents_create.
            </p>
          )}
          {proposeError && <p style={errorStyle}>{proposeError}</p>}
          {generateError && <p style={errorStyle}>{generateError}</p>}
          {generatedNotice && <p style={successNoticeStyle}>{generatedNotice}</p>}
          {(documentClass === 'corp_agreement' || proposal) && (
            <div style={{ marginTop: '0.25rem' }}>
              {proposal?.source === 'lk_placeholder' && (
                <p style={placeholderNoticeStyle}>
                  ⚠ AI-endpoint МИС временно недоступен — заполнены поля-заглушки. Исправьте
                  нули и [ЗАПОЛНИТЬ] перед генерацией.
                </p>
              )}
              {documentClass === 'installment_agreement' ? (
                <InstallmentProposalForm vars={installmentVars} onChange={setInstallmentVars} />
              ) : documentClass === 'corp_agreement' ? (
                <CorpAgreementProposalForm vars={corpVars} onChange={setCorpVars} />
              ) : (
                <TaxCertificateProposalForm
                  vars={taxVars}
                  onChange={setTaxVars}
                  onLoadFinancials={effectivePatientMisId ? handleLoadFinancials : undefined}
                  isLoadingFinancials={isLoadingFinancials}
                  payerLabel={selectedPatientName ?? patientQuery}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Document list */}
      <div style={listSectionStyle}>
        <header style={listHeaderStyle}>
          <h3 style={cardTitleStyle}>
            Сохранённые документы — {DOCUMENT_CLASS_LABELS[documentClass]}
          </h3>
          <button type="button" onClick={() => void loadDocuments()} style={ghostButtonStyle}>
            Обновить
          </button>
        </header>
        {isLoadingList && <p style={mutedStyle}>Загружаем список из МИС…</p>}
        {listError && <p style={errorStyle}>{listError}</p>}
        {!isLoadingList && !listError && documents.length === 0 && (
          <p style={mutedStyle}>
            Документов пока нет. Сгенерируйте первый — он появится здесь и в карточке пациента МИС.
          </p>
        )}
        {documents.length > 0 && (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Создан</th>
                <th style={thStyle}>Пациент</th>
                <th style={thStyle}>Шаблон</th>
                <th style={thStyle}>Статус</th>
                <th style={thStyle}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc, idx) => {
                const id = doc.generated_document_id ?? doc.document_id ?? doc.id ?? `row-${idx}`
                const patientName = doc.source_refs?.patient_full_name ?? doc.patient_full_name ?? '—'
                const templateLabel = doc.metadata?.template_name ?? doc.template_label ?? '—'
                const statusLabel =
                  doc.metadata?.lifecycle?.delivery_status ?? doc.human_status ?? doc.status ?? '—'
                return (
                  <tr key={id}>
                    <td style={tdStyle}>{doc.created_at ? doc.created_at.slice(0, 10) : '—'}</td>
                    <td style={tdStyle}>
                      {patientName}
                      {doc.patient_pcode && (
                        <div style={mutedSmallStyle}>{doc.patient_pcode}</div>
                      )}
                    </td>
                    <td style={tdStyle}>{templateLabel}</td>
                    <td style={tdStyle}>{statusLabel}</td>
                    <td style={tdStyle}>
                      {id && !String(id).startsWith('row-') && (
                        <button
                          type="button"
                          onClick={() => void handleDownload(String(id))}
                          style={ghostButtonStyle}
                        >
                          Скачать
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  )
}

// ─── styles ───────────────────────────────────────────────────────────────────────────────────────

const containerStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '1.5rem',
  padding: '1.5rem',
}

const panelStyle = { ...containerStyle, background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb' }

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap' as const,
  gap: '1rem',
}

const titleStyle = { margin: '0 0 0.5rem 0', fontSize: '1.4rem', fontWeight: 600 as const, color: '#111827' }
const mutedStyle = { margin: 0, color: '#6b7280', fontSize: '0.95rem', lineHeight: 1.5 }
const mutedSmallStyle = { color: '#9ca3af', fontSize: '0.8rem' }
const codeStyle = {
  margin: '0 0.25rem',
  padding: '0.05rem 0.35rem',
  background: '#f3f4f6',
  borderRadius: '4px',
  fontFamily: 'monospace' as const,
  fontSize: '0.9em',
}

const tabRowStyle = { display: 'flex', gap: '0.5rem' }

const tabStyle = {
  padding: '0.5rem 1rem',
  borderRadius: '999px',
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#374151',
  cursor: 'pointer' as const,
  fontWeight: 500 as const,
}
const activeTabStyle = { ...tabStyle, background: '#1f2937', color: '#fff', borderColor: '#1f2937' }

const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }

const cardStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '1.25rem',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '0.75rem',
}

const cardTitleStyle = { margin: 0, fontSize: '1.05rem', fontWeight: 600 as const, color: '#111827' }

const labelStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '0.35rem',
  color: '#374151',
  fontSize: '0.9rem',
  fontWeight: 500 as const,
}

const inlineRowStyle = { display: 'flex', flexWrap: 'wrap' as const, alignItems: 'flex-end', gap: '0.75rem' }
const inlineLabelStyle = { ...labelStyle, flex: '0 0 auto' }

const inputStyle = {
  padding: '0.55rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '0.95rem',
  fontFamily: 'inherit' as const,
  width: '100%',
  boxSizing: 'border-box' as const,
  minWidth: 0,
}

const textareaStyle = { ...inputStyle, resize: 'vertical' as const, minHeight: '96px', fontFamily: 'inherit' as const }
const selectStyle = { ...inputStyle, minWidth: '180px' }

const primaryButtonStyle = {
  padding: '0.55rem 1.1rem',
  borderRadius: '8px',
  border: '1px solid #2563eb',
  background: '#2563eb',
  color: '#fff',
  fontWeight: 600 as const,
  cursor: 'pointer' as const,
}
const successButtonStyle = { ...primaryButtonStyle, background: '#16a34a', borderColor: '#16a34a' }
const ghostButtonStyle = {
  padding: '0.45rem 0.9rem',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#1f2937',
  fontWeight: 500 as const,
  cursor: 'pointer' as const,
}
const disabledButtonStyle = {
  padding: '0.55rem 1.1rem',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  background: '#f3f4f6',
  color: '#9ca3af',
  fontWeight: 600 as const,
  cursor: 'not-allowed' as const,
}

const errorStyle = { margin: 0, padding: '0.5rem 0.75rem', borderRadius: '8px', background: '#fef2f2', color: '#b91c1c', fontSize: '0.9rem' }
const warningStyle = { ...errorStyle, background: '#fffbeb', color: '#b45309' }
const successNoticeStyle = { ...errorStyle, background: '#ecfdf5', color: '#047857' }
const placeholderNoticeStyle = { ...errorStyle, background: '#fefce8', color: '#92400e', margin: '0 0 0.5rem' }

const listSectionStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '1.25rem',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '0.75rem',
}

const listHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' as const, gap: '0.5rem' }

const tableStyle = { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' }
const thStyle = { padding: '0.5rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', textAlign: 'left' as const, color: '#6b7280', fontWeight: 600 as const, fontSize: '0.8rem', textTransform: 'uppercase' as const }
const tdStyle = { padding: '0.55rem 0.5rem', borderBottom: '1px solid #f3f4f6', color: '#1f2937', verticalAlign: 'top' as const }

const suggestionsStyle = {
  position: 'absolute' as const,
  top: '100%',
  left: 0,
  right: 0,
  background: '#fff',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  zIndex: 100,
  maxHeight: '220px',
  overflowY: 'auto' as const,
  marginTop: '2px',
}
const suggestionItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: 'none',
  background: 'transparent',
  textAlign: 'left' as const,
  cursor: 'pointer' as const,
  fontSize: '0.9rem',
  color: '#1f2937',
}
const suggestionIdStyle = { color: '#9ca3af', fontSize: '0.78rem', fontFamily: 'monospace' as const, marginLeft: '0.5rem' }
const patientSelectedStyle = { margin: 0, color: '#047857', fontSize: '0.9rem', fontWeight: 500 as const }

// proposal form styles
const proposalFormStyle = {
  background: '#f0fdf4',
  border: '1px solid #bbf7d0',
  borderRadius: '8px',
  padding: '0.75rem',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '0.5rem',
}
const proposalFormTitleStyle = { margin: 0, fontWeight: 600 as const, fontSize: '0.9rem', color: '#1f2937' }
const formGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '0.5rem',
}
const formFieldLabelStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '0.2rem',
}
const formFieldNameStyle = { fontSize: '0.78rem', color: '#4b5563', fontWeight: 500 as const }
const formInputStyle = {
  padding: '0.3rem 0.5rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '0.85rem',
  fontFamily: 'inherit' as const,
  background: '#fff',
  width: '100%',
  boxSizing: 'border-box' as const,
  minWidth: 0,
}
const calcRowStyle = {
  display: 'flex',
  gap: '1rem',
  fontSize: '0.85rem',
  color: '#374151',
  padding: '0.25rem 0',
  flexWrap: 'wrap' as const,
}
const scheduleHintStyle = { margin: 0, fontSize: '0.78rem', fontWeight: 600 as const, color: '#374151' }
const scheduleTableStyle = { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.82rem' }
const scheduleThStyle = { padding: '0.25rem 0.4rem', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left' as const, color: '#6b7280', fontWeight: 600 as const }
const scheduleTdStyle = { padding: '0.2rem 0.4rem', borderBottom: '1px solid #f3f4f6' }
const scheduleInputStyle = {
  width: '90px',
  padding: '0.2rem 0.35rem',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  fontSize: '0.82rem',
  fontFamily: 'inherit' as const,
}
const textareaFieldLabelStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '0.2rem',
}
const textareaInlineStyle = {
  ...formInputStyle,
  resize: 'vertical' as const,
}
const toggleStyle = {
  padding: '0.25rem 0.7rem',
  borderRadius: '999px',
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#374151',
  fontSize: '0.78rem',
  cursor: 'pointer' as const,
  fontWeight: 500 as const,
}
const toggleActiveStyle = {
  ...toggleStyle,
  background: '#1e3a5f',
  color: '#fff',
  borderColor: '#1e3a5f',
}
